import { Controller } from "@hotwired/stimulus"

const STORAGE_KEY = "gpcenarm-theme"

// The toggle's choice for this page's life, for when storage refuses to keep it: Turbo
// navigations would otherwise put the page's default back.
let sessionChoice = null

// The layout's inline script sets data-theme before paint; this only handles the
// toggle and keeps the icon in sync. Every storage call is guarded — in a private
// window localStorage throws rather than returning null.
export default class extends Controller {
  static targets = ["sun", "moon"]

  connect() {
    this.render()
    this.media = window.matchMedia("(prefers-color-scheme: dark)")
    this.onSystemChange = () => { if (!this.stored) this.render() }
    this.media.addEventListener("change", this.onSystemChange)
  }

  disconnect() {
    this.media?.removeEventListener("change", this.onSystemChange)
  }

  toggle() {
    const next = this.current === "dark" ? "light" : "dark"
    document.documentElement.setAttribute("data-theme", next)
    sessionChoice = next
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch (e) {
      // Theme still applies for this page view; it just will not survive a reload.
    }
    this.render()
  }

  render() {
    const dark = this.current === "dark"
    this.sunTarget.classList.toggle("hidden", !dark)
    this.moonTarget.classList.toggle("hidden", dark)
  }

  get stored() {
    try {
      return localStorage.getItem(STORAGE_KEY)
    } catch (e) {
      return null
    }
  }

  // The attribute wins when set; otherwise the system preference is what the CSS
  // is actually rendering, so the icon must follow it.
  get current() {
    const attr = document.documentElement.getAttribute("data-theme")
    if (attr === "dark" || attr === "light") return attr

    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  }
}

// Turbo swaps <body> but leaves <html> alone, so a page's default theme (the landing
// page opens light) would outlive it. Before each render, take the incoming page's
// default unless the visitor has chosen one.
document.addEventListener("turbo:before-render", (event) => {
  let stored = null
  try {
    stored = localStorage.getItem(STORAGE_KEY)
  } catch (e) {
    // Blocked storage: no choice was saved, so the page's default applies.
  }
  if (stored === "dark" || stored === "light" || sessionChoice) return

  const fallback = event.detail.newBody.dataset.defaultTheme
  if (fallback) {
    document.documentElement.setAttribute("data-theme", fallback)
  } else {
    document.documentElement.removeAttribute("data-theme")
  }
})
